import './BdaApplicationItem.css';

import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { memo, useMemo } from 'react'
import { Badge, Card, Col, Row } from 'react-bootstrap'
import classNames from 'classnames';
import UserItem from '../common/UserItem';
import { formatDate } from '../../utils';
import { BdaApplication } from '../../models/BdaApplication';
import { Flex, Tooltip } from 'antd';
import { useAuth } from '../../contexts/auth';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import AppTag from '../common/AppTag';

type Props = {
  application: BdaApplication;
  projectMode?: boolean;
  actions?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onApplicationToggle?: React.MouseEventHandler<HTMLButtonElement>;
  onTemplateAction?: React.MouseEventHandler<HTMLButtonElement>;
  onApplicationEdit?: React.MouseEventHandler<HTMLButtonElement>;
  onApplicationDelete?: React.MouseEventHandler<HTMLButtonElement>;
}

function BdaApplicationItem({
  application,
  projectMode,
  actions,
  onClick,
  onApplicationToggle,
  onTemplateAction,
  onApplicationEdit,
  onApplicationDelete,
}: Props) {

  const { loggedUser } = useAuth();
  const date = formatDate(application.createDateTime!);

  // TODO should we always show actions for any application that is visible to user
  const showActions = useMemo(() => !application.template || application.owner?.id === loggedUser?.sub, [application, loggedUser]);

  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));

  return (
    <Card className="application-item h-100" onClick={onClick}>
      <Card.Header className={classNames("application-item-header bg-application", { "bg-template": application.template })}>
        <Row className="gx-0 align-items-start">
          <Col className="application-date">
            <b>Created</b>&nbsp;
            <span>{date}</span>
          </Col>
          {application.template && <Badge bg="dark" className="shadow w-auto text-uppercase me-2">Template</Badge>}
          <Badge className="shadow w-auto">{application.accessLevel}</Badge>
        </Row>

        <FontAwesomeIcon className="application-icon" icon={solid('laptop-code')} />

        <div className="position-relative text-center py-3">
          <Tooltip title={application.name} mouseEnterDelay={0.3}>
            <h5 className="application-title">{application.name}</h5>
          </Tooltip>
          <p className="application-description line-clamp-2 mt-2">{application.description}</p>
        </div>

        <Row className="gx-0 justify-content-center">
          {actions || (
            <Col className="text-center" xs={9}>
              {!application.template && (
                <Tooltip title={application.status === "RUNNING" || application.status === "PARTIAL" || application.status === "STARTING" ? "Pause" : "Play"} mouseEnterDelay={0.3}>
                  <button className="circle-button" onClick={onApplicationToggle} disabled={application.status === "STARTING"} style={application.status === "STARTING" ? { display: "inline-flex" } : undefined}>
                    <FontAwesomeIcon size="sm" icon={application.status === "RUNNING" || application.status === "PARTIAL" || application.status === "STARTING" ? solid('pause') : solid('play')} />
                  </button>
                </Tooltip>
              )}
              <Tooltip title={`Create ${application.template ? applicationLabels.singular : 'Template'}`} mouseEnterDelay={0.3}>
                <button className="circle-button ms-2" onClick={onTemplateAction}>
                  {application.template
                    ? <FontAwesomeIcon size="sm" icon={solid('plus')} />
                    : <FontAwesomeIcon size="sm" icon={solid('copy')} />
                  }
                </button>
              </Tooltip>
              {showActions && (
                <>
                  <Tooltip title="Edit" mouseEnterDelay={0.3}>
                    <button className="circle-button ms-2" onClick={onApplicationEdit}>
                      <FontAwesomeIcon size="sm" icon={solid('pen')} />
                    </button>
                  </Tooltip>
                  <Tooltip title="Delete" mouseEnterDelay={0.3}>
                    <button className="circle-button ms-2" onClick={onApplicationDelete}>
                      <FontAwesomeIcon size="sm" icon={solid('trash')} />
                    </button>
                  </Tooltip>
                </>
              )}
            </Col>
          )}
        </Row>
      </Card.Header>
      <Card.Body className={classNames("p-2", { "template-body": application.template })}>
        <UserItem className="mb-2" user={application.owner!} organization={application.organization!} />

        <Flex className="tags-wrapper">
          {application.tags.length === 0 && <AppTag noTags />}
          {application.tags.map(tag => <AppTag className="me-1" tag={tag} key={tag} />)}
        </Flex>

        <Row className="mt-2">
          <Col xs={4} className="text-center">
            <Tooltip title="Services" mouseEnterDelay={0.25}>
              <div className="application-stat">
                <FontAwesomeIcon icon={solid('cubes')} fixedWidth />
                <span className="ms-2">{application.serviceCount}</span>
              </div>
            </Tooltip>
          </Col>
          <Col xs={4} className="text-center">
            <Tooltip title="Datasets" mouseEnterDelay={0.25}>
              <div className="application-stat">
                <FontAwesomeIcon icon={solid('database')} fixedWidth />
                <span className="ms-2">{application.datasetCount}</span>
              </div>
            </Tooltip>
          </Col>
          <Col xs={4} className="text-center">
            <Tooltip title="Models" mouseEnterDelay={0.25}>
              <div className="application-stat">
                <FontAwesomeIcon icon={solid('graduation-cap')} fixedWidth />
                <span className="ms-2">{application.modelCount}</span>
              </div>
            </Tooltip>
          </Col>
        </Row>
      </Card.Body>

      {!application.template && (
        <Card.Footer className={classNames("application-status", application.status || 'READY', { "detail-status": projectMode })}>
          <span>{application.status || "READY"}</span>
        </Card.Footer>
      )}
    </Card>
  )
}

export default memo(BdaApplicationItem);